import { reverse } from 'named-urls';

import { getEntityTranslation } from 'stores/EntityDetails/utils';
import { EntityModules } from 'core/constants';
import routes from 'core/routes';
import SessionUser from 'stores/Session/SessionUser';
import { ADMIN_AND_ACCOUNT_MANAGERS } from 'utils/roles/roleGroups';

import { SettingsNavNode } from './components/SettingsNavTree';

export const CONFIGURABLE_OBJECT_MODULES: EntityModules[] = [
  EntityModules.Events,
  EntityModules.Sites,
  EntityModules.Assets,
  EntityModules.Contacts,
  EntityModules.RisksAssessments,
  EntityModules.Risks,
];

export const settingsPathMatches = (pathname: string, route: string): boolean => {
  if (pathname.startsWith(route)) return true;
  const noTrailing = route.endsWith('/') ? route.slice(0, -1) : route;
  return pathname === noTrailing;
};

export const canSeeOrganisationSettings = (user: SessionUser): boolean =>
  ADMIN_AND_ACCOUNT_MANAGERS.includes(user.role);

export const hasNotificationsLicence = (user: SessionUser): boolean =>
  Boolean(user.licenses.workflows || user.licenses.events);

export const firstOwnedObjectModule = (user: SessionUser): EntityModules | undefined =>
  user.showObjectManager
    ? CONFIGURABLE_OBJECT_MODULES.find((module) => user.activeEntityModules.includes(module))
    : undefined;

const toolNode = (
  moduleName: EntityModules,
  label: string,
  route: string,
  key: string,
  pathname: string
): SettingsNavNode => {
  const to = reverse(route, { moduleName });
  return { key: `${moduleName}-${key}`, label, to, isSelected: pathname.startsWith(to) };
};

export const buildObjectToolNodes = (moduleName: EntityModules, pathname: string): SettingsNavNode[] => {
  const tools: SettingsNavNode[] = [];

  if (moduleName === EntityModules.Events) {
    tools.push(
      toolNode(moduleName, 'Forms', routes.dashboard.settings.objects.details.formBuilder.toString(), 'forms', pathname)
    );
  }

  tools.push(
    toolNode(
      moduleName,
      'Field Manager',
      routes.dashboard.settings.objects.details.fieldManager,
      'field-manager',
      pathname
    ),
    toolNode(
      moduleName,
      'Layout Editor',
      routes.dashboard.settings.objects.details.layoutEditor,
      'layout-editor',
      pathname
    )
  );

  if (moduleName === EntityModules.Events) {
    tools.push(
      toolNode(
        moduleName,
        'Pipelines',
        routes.dashboard.settings.objects.details.pipelines.toString(),
        'pipelines',
        pathname
      ),
      toolNode(
        moduleName,
        'Safety Metrics',
        routes.dashboard.settings.objects.details.safetyMetrics,
        'safety-metrics',
        pathname
      ),
      toolNode(
        moduleName,
        'Assignment Rules',
        routes.dashboard.settings.objects.details.assignmentRules,
        'assignment-rules',
        pathname
      )
    );
  }

  return tools;
};

export const settingsObjectLabel = (moduleName: EntityModules, user: SessionUser | null | undefined): string =>
  moduleName === EntityModules.Risks && user?.risksAssessmentsModuleEnabled
    ? 'Risk Register'
    : getEntityTranslation[moduleName].plural;

export const buildObjectNode = (
  moduleName: EntityModules,
  pathname: string,
  user: SessionUser | null | undefined
): SettingsNavNode => ({
  key: `object-${moduleName}`,
  label: settingsObjectLabel(moduleName, user),
  children: buildObjectToolNodes(moduleName, pathname),
});

export const resolveDefaultSettingsPath = (user: SessionUser): string => {
  if (canSeeOrganisationSettings(user)) {
    return routes.dashboard.settings.users.toString();
  }

  const firstObject = firstOwnedObjectModule(user);
  if (firstObject) {
    return firstObject === EntityModules.Events
      ? reverse(routes.dashboard.settings.objects.details.formBuilder.toString(), { moduleName: firstObject })
      : reverse(routes.dashboard.settings.objects.details.fieldManager, { moduleName: firstObject });
  }

  if (user.showObjectManager) {
    return routes.dashboard.settings.userFields.toString();
  }

  return hasNotificationsLicence(user)
    ? routes.dashboard.settings.notifications.toString()
    : routes.dashboard.settings.security.toString();
};
